import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HEALTH_URL } from "./api";

/**
 * Tells the person when the app they are looking at is out of date.
 *
 * A single-page app keeps running whatever bundle it loaded, so after a release
 * someone can sit on yesterday's version for hours — hitting bugs that were
 * fixed, or calling an endpoint that has moved. They then report a fault that
 * nobody can reproduce.
 *
 * So the running app asks the server which build it is serving. When that stops
 * matching the build this page started with, it says so and offers a reload. It
 * never reloads on its own: someone half-way through writing a daily log should
 * not lose it to a deploy.
 */
const CHECK_MS = 3 * 60 * 1000;

export function UpdatePrompt() {
  const { t } = useTranslation();
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mine = "";
    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch(HEALTH_URL, { credentials: "include" });
        if (!res.ok) return;
        const h = (await res.json()) as { sha?: string };
        const sha = h?.sha ?? "";
        if (!sha || cancelled) return;
        if (!mine) {
          mine = sha; // the build this page started with
          return;
        }
        if (sha !== mine) setStale(true);
      } catch {
        // offline or mid-deploy — not knowing is not the same as being stale
      }
    };

    void check();
    const timer = setInterval(() => void check(), CHECK_MS);
    // A tab left open all day should check the moment it is looked at again.
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  if (!stale || dismissed) return null;

  return (
    <div className="updbar" role="status">
      <span className="updbar__dot" />
      <span>{t("update.available")}</span>
      <button className="ax-btn ax-btn--primary ax-btn--sm" onClick={() => location.reload()}>
        {t("update.reload")}
      </button>
      <button className="updbar__x" onClick={() => setDismissed(true)} aria-label={t("common.close")}>
        ✕
      </button>
    </div>
  );
}
